import { useTranslation } from 'react-i18next';

import { cn } from '@workspace/ui/lib/utils';

import Icon from '~/components/Icon/Icon';

/**
 * What the user is shown when a boundary catches something it cannot recover
 * from on its own.
 *
 * Dressed in the tenant's own tokens rather than in defaults. The typography
 * tokens carry the family along with the size — `--text-500-medium` expands to
 * `500 20px / 21px var(--fontFamily2)` — which is what keeps this screen out of
 * the browser's serif default: `body` sets no family of its own, and an error
 * screen sits outside every layout container that does.
 *
 * Reloading is the default action, and for a boundary it is the honest one: the
 * failures that reach one are chunks that will not arrive and renders that
 * threw, and `React.lazy` keeps a rejected import rejected for the life of the
 * document.
 *
 * `onRetry` covers the callers that can do better — a failed request the page
 * can simply re-issue, where discarding the document would be a heavier answer
 * than the problem deserves.
 */
export const ErrorFallback = ({
  className,
  title,
  description,
  onRetry,
  secondaryAction,
}: {
  className?: string;
  title?: string;
  description?: string;
  /** Retries in place. Defaults to reloading the document. */
  onRetry?: () => void;
  secondaryAction?: { label: string; onClick: () => void };
}) => {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-4 px-6 text-center',
        className,
      )}
    >
      <span className="flex size-14 items-center justify-center rounded-full bg-surface-transparent-2 text-text-secondary">
        <Icon type="warning" size={28} />
      </span>

      <p className="m-0 text-text-primary [font:var(--text-500-medium)]">
        {title ?? t('phrases:errorOccurred', 'Something went wrong')}
      </p>

      {description && (
        <p className="m-0 -mt-2 text-text-secondary [font:var(--text-300-regular)]">
          {description}
        </p>
      )}

      <button
        type="button"
        className="mt-2 cursor-pointer rounded-full border-0 bg-action-brand px-8 py-4 text-text-in-button transition-opacity [font:var(--text-300-medium)] hover:opacity-90"
        onClick={onRetry ?? (() => window.location.reload())}
      >
        {t('phrases:tryAgain', 'Try again')}
      </button>

      {secondaryAction && (
        <button
          type="button"
          className="cursor-pointer border-0 bg-transparent p-0 text-text-secondary underline [font:var(--text-300-medium)]"
          onClick={secondaryAction.onClick}
        >
          {secondaryAction.label}
        </button>
      )}
    </div>
  );
};
